/**
 * Unités de conditionnement (« unités multiples ») d'un article — logique pure.
 *
 * Un article a UNE unité de base (la pièce, `Article.unit`) dont le prix
 * (`salePriceHt/Ttc`) est le prix unitaire de référence. En plus, il peut
 * définir des **conditionnements** : « Carton = 12 pièces », « Lot = 6 pièces ».
 * Chaque conditionnement porte un `factor` = nombre de pièces de base qu'il
 * contient.
 *
 * Règle produit (validée) : le prix d'un conditionnement est **dérivé**
 * automatiquement (prix pièce × facteur), jamais saisi à la main. Sur une ligne
 * de document, choisir « Carton » pose donc un prix unitaire effectif de
 * `prix_pièce × 12` ET fige le facteur pour que le STOCK décompte bien 12 pièces
 * par carton. Le total document (`qté × PU`) reste juste sans toucher au calcul
 * de ligne : c'est le PU qui porte le facteur, pas la quantité.
 *
 * Un article SANS conditionnement se comporte exactement comme avant : facteur
 * implicite 1, aucune unité secondaire, aucun sélecteur affiché.
 */

import { isWholeUnitQuantity } from './article-units';

/** Un conditionnement d'article : un libellé libre + un nombre de pièces. */
export interface PackagingUnit {
  /** Identifiant stable (clé React + rapprochement). Généré à la saisie. */
  id: string;
  /** Libellé affiché, ex. « Carton », « Lot », « Palette ». */
  label: string;
  /** Nombre de pièces de base contenues. Entier ≥ 2. */
  factor: number;
}

/** Facteur maximal d'un conditionnement (garde-fou de saisie). */
export const MAX_PACKAGING_FACTOR = 1_000_000;

/** Nombre maximal de conditionnements par article (garde-fou d'UI). */
export const MAX_PACKAGING_UNITS = 20;

/** Facteur de l'unité de base : une pièce vaut une pièce. */
export const BASE_UNIT_FACTOR = 1;

/**
 * `true` si `factor` est un facteur de conditionnement valide : un entier ≥ 2.
 * On refuse 1 (ce serait l'unité de base) et les valeurs non entières (un
 * carton contient un nombre entier de pièces).
 */
export function isValidPackagingFactor(factor: number): boolean {
  return (
    Number.isInteger(factor) && factor >= 2 && factor <= MAX_PACKAGING_FACTOR
  );
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/**
 * Prix unitaire effectif d'un conditionnement = prix de la pièce × facteur.
 * Arrondi aux millimes. Comme `factor` est entier et le prix pièce déjà en
 * millimes, le produit est exact — l'arrondi est un simple filet de sécurité.
 */
export function packagingUnitPrice(basePrice: number, factor: number): number {
  return round3(Number(basePrice) * Number(factor));
}

/**
 * Prix de la pièce reconstruit depuis un prix effectif figé sur une ligne et
 * son facteur. Sert au changement d'unité sur une ligne existante, sans avoir
 * à relire l'article. `factor ≤ 0` retombe sur le prix effectif (pas de
 * division par zéro).
 */
export function basePriceFromPackaging(
  effectivePrice: number,
  factor: number | null | undefined,
): number {
  const safeFactor = Number(factor);
  if (!Number.isFinite(safeFactor) || safeFactor <= 0) {
    return round3(Number(effectivePrice));
  }
  return round3(Number(effectivePrice) / safeFactor);
}

/**
 * `true` si l'unité choisie sur une ligne se compte en nombres ENTIERS : soit
 * l'unité de base est déjà entière (carton legacy `box`), soit un
 * conditionnement (facteur > 1) — on ne vend pas « 1,5 carton ».
 */
export function lineRequiresWholeQuantity(
  unit: string | null | undefined,
  unitFactor: number | null | undefined,
): boolean {
  const factor = Number(unitFactor);
  if (Number.isFinite(factor) && factor > 1) return true;
  return isWholeUnitQuantity(unit);
}

/**
 * Normalise une liste de conditionnements venue d'un formulaire ou d'un JSONB.
 *
 * - libellé vide (une fois trimé) → rejeté ;
 * - facteur invalide (non entier, < 2, hors borne) → rejeté ;
 * - deux conditionnements de MÊME facteur → on garde le premier (choisir un
 *   carton de 12 doit être sans ambiguïté au moment de retrouver le libellé) ;
 * - `id` conservé s'il est fourni, sinon dérivé de la position (déterministe,
 *   la fonction reste pure) ;
 * - liste plafonnée à {@link MAX_PACKAGING_UNITS}.
 */
export function sanitizePackagingUnits(input: unknown): PackagingUnit[] {
  if (!Array.isArray(input)) return [];
  const seenFactors = new Set<number>();
  const result: PackagingUnit[] = [];
  input.forEach((raw, index) => {
    if (raw == null || typeof raw !== 'object') return;
    const entry = raw as Record<string, unknown>;
    const label = typeof entry['label'] === 'string' ? entry['label'].trim() : '';
    const factor = Number(entry['factor']);
    if (!label) return;
    if (!isValidPackagingFactor(factor)) return;
    if (seenFactors.has(factor)) return;
    seenFactors.add(factor);
    const providedId =
      typeof entry['id'] === 'string' && entry['id'].trim()
        ? (entry['id'] as string)
        : `u${index}`;
    result.push({ id: providedId, label, factor });
  });
  return result.slice(0, MAX_PACKAGING_UNITS);
}

/**
 * Retrouve le conditionnement correspondant à un facteur figé sur une ligne.
 * Utilisé pour ré-afficher le bon libellé quand on rouvre un document.
 */
export function findPackagingByFactor(
  units: readonly PackagingUnit[] | null | undefined,
  factor: number | null | undefined,
): PackagingUnit | undefined {
  if (!units || !Number.isFinite(Number(factor))) return undefined;
  return units.find((unit) => unit.factor === Number(factor));
}
